import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { apiClient } from "../utils/const";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";   // ✅ added
import { useTheme } from "./ThemeContext";

const AddPayment = () => {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [lastTransaction, setLastTransaction] = useState(null);
  const [selectedUserOption, setSelectedUserOption] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [numMonths, setNumMonths] = useState(1);

  const navigate = useNavigate();
  const [schemeType, setSchemeType] = useState(localStorage.getItem("schemeType") || "mandal"); 
  const { dark } = useTheme();
  
  // fetch only users for current scheme
  const fetchUsers = async () => {
    setError(null);
    const endpoint =
      schemeType === "kanchan"
      ? "/kanchan/kanchan_payment_users.php"
      : "/mandal/mandal_payment_users.php";

    try {
      const res = await apiClient.post(endpoint);
      // console.log(res.data);
      if (res.data.message === "radha" && res.data.data?.users) {
        setUsers(res.data.data.users);
      } else {
        toast.error( "Failed to load users");
      }
    } catch (err) {
      console.error("Failed to load users", err);
      toast.error("Failed to fetch users.");
    }
  };

  // Listen for scheme changes
  useEffect(() => {
    const handleSchemeChange = () => {
      const currentScheme = localStorage.getItem("schemeType") || "mandal";
      setSchemeType(currentScheme);
      setSelectedUser(null);
      setAmount("");
    };

    // Listen for scheme change events
    window.addEventListener("schemeChanged", handleSchemeChange);
    
    // Also check on component mount
    handleSchemeChange();

    return () => {
      window.removeEventListener("schemeChanged", handleSchemeChange);
    };
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [schemeType]);

  // Set amount when user changes: mandal = monthly_amount, kanchan = user enters manually
  useEffect(() => {
    if (!selectedUser) return;
    if (schemeType === "mandal") {
      setAmount(selectedUser.monthly_amount || "");
    } else {
      setAmount("");
    }
  }, [selectedUser, schemeType]);

  const getFullName = (user) =>
    [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" ").trim();

  const selectStyles = useMemo(
    () => ({
      control: (provided, state) => ({
        ...provided,
        minHeight: 44,
        backgroundColor: dark ? "rgba(255,255,255,0.05)" : "#fff",
        borderColor: state.isFocused ? "var(--bs-primary)" : "var(--bs-border-color)",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(102, 126, 234, 0.15)" : "none",
        color: dark ? "#f1f1f1" : "#212529",
        borderRadius: 12,
        transition: "all 0.2s ease",
      }),
      input: (provided) => ({
        ...provided,
        color: dark ? "#f1f1f1" : "#212529",
      }),
      singleValue: (provided) => ({
        ...provided,
        color: dark ? "#f1f1f1" : "#212529",
        fontWeight: 500,
      }),
      placeholder: (provided) => ({
        ...provided,
        color: dark ? "rgba(255,255,255,0.5)" : "#6c757d",
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: "6px 12px",
      }),
      dropdownIndicator: (provided, state) => ({
        ...provided,
        color: state.isFocused ? "var(--bs-primary)" : dark ? "rgba(255,255,255,0.6)" : "#6c757d",
        paddingRight: 12,
      }),
      clearIndicator: (provided) => ({
        ...provided,
        paddingRight: 12,
        color: dark ? "rgba(255,255,255,0.6)" : "#6c757d",
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: dark ? "#1f1f1f" : "#fff",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid var(--bs-border-color)",
        boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
        marginTop: 8,
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? "var(--bs-primary)"
          : state.isFocused
          ? "var(--bs-primary-bg-subtle)"
          : "transparent",
        color: state.isSelected ? "#fff" : dark ? "#f1f1f1" : "#212529",
        fontWeight: state.isSelected ? 600 : 500,
        padding: "10px 14px",
        transition: "background 0.15s ease",
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
      }),
    }),
    []
  );

  const userOptions = users.map((user) => ({
    value: user.id,
    label: `${user.card_no || user.id} ${getFullName(user)} ${user.phone || ""}`.trim(),
    data: user,
  }));

  const handleUserChange = (selected) => {
    if (!selected) {
      setSelectedUser(null);
      setSelectedUserOption(null);
      setAmount("");
      return;
    }
    const user = selected.data;
    setSelectedUser(user);
    setSelectedUserOption(selected);
    setNumMonths(1);
    setPaymentType("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setLastTransaction({
      amount: user.last_payment_amount,
      date: user.last_payment_date,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      toast.warn("⚠️ Please select a user first.");
      return;
    }
    // console.log(selectedUser.status);
    const parsedAmount =
      schemeType === "kanchan"
        ? parseInt(amount, 10)
        : parseFloat(amount);

    const payload = {
      user_id: selectedUser.id,
      card_no: selectedUser.card_no,
      amount: parsedAmount,
      payment_type: paymentType,
      payment_date: paymentDate,
      status:selectedUser.status
    };
    if (schemeType === "kanchan") {
      payload.scheme_id = selectedUser.scheme_id;
      payload.no_of_months = numMonths;
    }
    // console.log(payload)
    try {
      const res = await apiClient.post(
        schemeType === "kanchan"
          ? "/kanchan/create_kanchan_payments.php"
          : "/mandal/create_mandal_payments.php",
        payload
      );
      const data = res.data;
      // console.log(data);

      if (data.message === "radha") {
        toast.success("Installment added successfully ");
        // Clear all form data
        setSelectedUser(null);
        setSelectedUserOption(null);
        setAmount("");
        setPaymentType("");
        setPaymentDate(new Date().toISOString().slice(0, 10));
        setNumMonths(1);
        // Do nothing here; navigation to PaymentList is now handled by a button in the header.
      } else if (data.message === "duplicate") {
        const p = data.data.existing_payment;
        toast.info(
          `⚠️ Payment already exists for ${p.first_name}\n` +
          `• Amount: ₹${p.amount}\n` +
          `• Type: ${p.payment_type}\n` +
          `• Payment Date: ${p.payment_date}\n` +
          `• Created On: ${p.created_on}`
        );
      } else {
        toast.error("❌ Failed to add payment: " + ("Unknown error"));
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error("❌ Error adding payment");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="page-content py-2">
        <div className="container-xxl px-2">
          <div className="row">
            <div className="col-12">
              <div className="card shadow-sm mb-2">
                <div className="card-header add-user-bg text-white fw-bold fs-4 px-3 py-2">
                  Add Payment
                </div>

                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="mb-2">Select Member</label>
                      <Select
                        options={userOptions}
                        value={selectedUserOption}
                        onChange={handleUserChange}
                        placeholder="Search Member..."
                        isClearable
                        styles={selectStyles}
                        className="react-select-member"
                        classNamePrefix="react-select"
                        menuPortalTarget={typeof window !== "undefined" ? document.body : undefined}
                        formatOptionLabel={(option) => {
                          const u = option.data;
                          const name = getFullName(u);
                          return (
                            <div style={{ whiteSpace: "nowrap" }}>
                              Card No: {u.card_no || "-"}, Name: {name || "-"}, Phone: {u.phone || "-"}
                            </div>
                          );
                        }}
                        filterOption={(candidate, rawInput) => {
                          const input = String(rawInput || "").trim().toLowerCase();
                          if (!input) return true;
                          const u = candidate?.data || {};
                          const idStr = String(u.id ?? candidate.value ?? "").toLowerCase();
                          const cardNoStr = String(u.card_no || "").toLowerCase();
                          const nameStr = String(getFullName(u) || "").toLowerCase();
                          const phoneStr = String(u.phone || "").toLowerCase();
                          const labelStr = String(candidate.label || "").toLowerCase();
                          return (
                            idStr.includes(input) ||
                            cardNoStr.includes(input) ||
                            nameStr.includes(input) ||
                            phoneStr.includes(input) ||
                            labelStr.includes(input)
                          );
                        }}
                        noOptionsMessage={() => "No matches"}
                      />
                      {selectedUser && (
                        <div className="form-text text-muted mt-1">
                          {lastTransaction?.date
                            ? `Last transaction date: ${lastTransaction.date}`
                            : "No previous transactions"}
                        </div>
                      )}
                    </div>
                  </div>

                  <form className="mt-3" onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label>Amount (₹):</label>
                        <input
                          type="number"
                          step="1"
                          className="form-control"
                          value={amount}
                          onChange={(e) => {
                            const { value } = e.target;
                            if (schemeType === "kanchan") {
                              if (value === "") {
                                setAmount("");
                              } else {
                                const rounded = Math.round(Number(value));
                                setAmount(Number.isNaN(rounded) ? "" : String(rounded));
                              }
                            } else {
                              setAmount(value);
                            }
                          }}
                          required
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label>Payment Type:</label>
                        <select
                          className="form-select"
                          value={paymentType}
                          onChange={(e) => setPaymentType(e.target.value)}
                          required
                        >
                          <option value="">-- Select --</option>
                          <option value="Cash">Cash</option>
                          <option value="UPI">UPI</option>
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Net Banking">Net Banking</option>
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label>Payment Date:</label>
                        <input
                          type="date"
                          className="form-control"
                          value={paymentDate}
                          onChange={(e) => setPaymentDate(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    {schemeType === "kanchan" && (
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label>Number of Months/Grams:</label>
                          <input
                            type="number"
                            className="form-control"
                            min="1"
                            max="12"
                            value={numMonths}
                            onChange={(e) => setNumMonths(Number(e.target.value))}
                            required
                          />
                        </div>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-2 d-flex justify-content-between align-items-center">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setSelectedUser(null)}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn add-user-btn"
                            disabled={!selectedUser}
                          >
                            Save Payment
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          .add-user-bg {
            background-color: #4B2C3B !important;
          }
          .add-user-btn {
            background-color: #4B2C3B !important;
            color: #fff !important;
            border: none;
            transition: background 0.2s;
          }
          .add-user-btn:hover {
            background-color: #D3AF37 !important;
            color: #fff !important;
          }
        `}</style>
      </div>
    </div>
  );
};

export default AddPayment;
